import React, { useEffect, useRef, useState } from "react";

export interface RegionalLink {
    name: string;
    href: string;
}

interface RegionalChurchesMarqueeProps {
    links: RegionalLink[];
    speed?: number; // pixels per frame
}

export const RegionalChurchesMarquee: React.FC<RegionalChurchesMarqueeProps> = ({
    links,
    speed = 0.75,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const setRef = useRef<HTMLDivElement | null>(null);
    const [isInteracting, setIsInteracting] = useState(false);
    const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const startScrollLeftRef = useRef(0);
    const movedDistanceRef = useRef(0);

    useEffect(() => {
        const container = containerRef.current;
        const setEl = setRef.current;
        if (!container || !setEl) return;

        // Position at the middle set so user can drag left or right immediately
        const singleWidth = setEl.offsetWidth;
        if (container.scrollLeft === 0 && singleWidth > 0) {
            container.scrollLeft = singleWidth;
        }

        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (prefersReducedMotion) return;

        let animationFrameId: number;

        const loop = () => {
            if (!isInteracting && container && setEl) {
                const width = setEl.offsetWidth;
                if (width > 0) {
                    container.scrollLeft += speed;

                    // Wrap around seamlessly
                    if (container.scrollLeft >= width * 2) {
                        container.scrollLeft -= width;
                    } else if (container.scrollLeft <= 0) {
                        container.scrollLeft += width;
                    }
                }
            }
            animationFrameId = requestAnimationFrame(loop);
        };

        animationFrameId = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (resumeTimeoutRef.current) {
                clearTimeout(resumeTimeoutRef.current);
            }
        };
    }, [isInteracting, speed]);

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        // Only primary button
        if (e.button !== 0 && e.pointerType === "mouse") return;

        setIsInteracting(true);
        if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);

        isDraggingRef.current = true;
        startXRef.current = e.clientX;
        movedDistanceRef.current = 0;
        if (containerRef.current) {
            startScrollLeftRef.current = containerRef.current.scrollLeft;
        }
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDraggingRef.current || !containerRef.current) return;

        const delta = e.clientX - startXRef.current;
        movedDistanceRef.current = Math.abs(delta);
        containerRef.current.scrollLeft = startScrollLeftRef.current - delta;

        // Wrap around seamlessly while dragging
        const setEl = setRef.current;
        if (setEl) {
            const width = setEl.offsetWidth;
            if (width > 0) {
                if (containerRef.current.scrollLeft >= width * 2) {
                    containerRef.current.scrollLeft -= width;
                    startScrollLeftRef.current -= width;
                } else if (containerRef.current.scrollLeft <= 0) {
                    containerRef.current.scrollLeft += width;
                    startScrollLeftRef.current += width;
                }
            }
        }
    };

    const endInteraction = () => {
        isDraggingRef.current = false;
        if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = setTimeout(() => {
            setIsInteracting(false);
        }, 1500);
    };

    const handleLinkClick = (e: React.MouseEvent) => {
        // If the user was dragging, prevent following link
        if (movedDistanceRef.current > 5) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    return (
        <nav
            aria-label="Regional churches"
            className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_4%,black_96%,transparent)]"
            onMouseEnter={() => setIsInteracting(true)}
            onMouseLeave={endInteraction}
            onFocus={() => setIsInteracting(true)}
            onBlur={endInteraction}
        >
            <div
                ref={containerRef}
                className="flex w-full overflow-x-auto scrollbar-none cursor-grab active:cursor-grabbing select-none py-1"
                style={{ WebkitOverflowScrolling: "touch" }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endInteraction}
                onPointerCancel={endInteraction}
                onTouchStart={() => setIsInteracting(true)}
                onTouchEnd={endInteraction}
            >
                {/* First set - used for reference width measurement */}
                <div ref={setRef} className="flex shrink-0 gap-2 pr-2">
                    {links.map((link) => (
                        <a
                            key={`set0-${link.name}`}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleLinkClick}
                            aria-label={`${link.name} - opens in new tab`}
                            className="shrink-0 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-xs text-gray-300 transition-colors hover:border-green-300/50 hover:bg-white/10 hover:text-white"
                        >
                            {link.name}
                        </a>
                    ))}
                </div>

                {/* Second set */}
                <div className="flex shrink-0 gap-2 pr-2" aria-hidden="true">
                    {links.map((link) => (
                        <a
                            key={`set1-${link.name}`}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            tabIndex={-1}
                            onClick={handleLinkClick}
                            className="shrink-0 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-xs text-gray-300 transition-colors hover:border-green-300/50 hover:bg-white/10 hover:text-white"
                        >
                            {link.name}
                        </a>
                    ))}
                </div>

                {/* Third set */}
                <div className="flex shrink-0 gap-2 pr-2" aria-hidden="true">
                    {links.map((link) => (
                        <a
                            key={`set2-${link.name}`}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            tabIndex={-1}
                            onClick={handleLinkClick}
                            className="shrink-0 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-xs text-gray-300 transition-colors hover:border-green-300/50 hover:bg-white/10 hover:text-white"
                        >
                            {link.name}
                        </a>
                    ))}
                </div>
            </div>
        </nav>
    );
};

export default RegionalChurchesMarquee;
